import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Plus, Edit, ArrowLeft, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegulatoryReferencesManagement = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  
  const [form, setForm] = useState({
    reference_type: 'norme',
    code: '',
    title: '',
    description: '',
    official_text: '',
    explanation: '',
    measurement_protocol: '',
    why_mandatory: '',
    tags: ''
  });

  useEffect(() => {
    loadReferences();
  }, []);

  const loadReferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/references`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferences(response.data || []);
    } catch (error) {
      console.error('Error loading references:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('tkale_token');
      const data = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : []
      };
      
      if (editingRef) {
        await axios.put(
          `${API}/admin/references/${editingRef.reference_id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Référence mise à jour');
      } else {
        await axios.post(
          `${API}/admin/references/create`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Référence créée');
      }
      
      setShowModal(false);
      resetForm();
      loadReferences();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ref) => {
    setEditingRef(ref);
    setForm({
      reference_type: ref.reference_type,
      code: ref.code,
      title: ref.title,
      description: ref.description,
      official_text: ref.official_text || '',
      explanation: ref.explanation || '',
      measurement_protocol: ref.measurement_protocol || '',
      why_mandatory: ref.why_mandatory || '',
      tags: ref.tags ? ref.tags.join(', ') : ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      reference_type: 'norme',
      code: '',
      title: '',
      description: '',
      official_text: '',
      explanation: '',
      measurement_protocol: '',
      why_mandatory: '',
      tags: ''
    });
    setEditingRef(null);
  };

  const getStatusBadge = (status) => {
    const config = {
      'active': { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" />, label: 'Active' },
      'replaced': { color: 'bg-orange-100 text-orange-800', icon: <AlertTriangle className="w-3 h-3" />, label: 'Remplacée' },
      'cancelled': { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3 h-3" />, label: 'Annulée' }
    };
    const cfg = config[status] || config['active'];
    return (
      <Badge className={cfg.color}>
        {cfg.icon}
        <span className="ml-1">{cfg.label}</span>
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      'norme': 'bg-blue-100 text-blue-800',
      'loi': 'bg-purple-100 text-purple-800',
      'arrete': 'bg-teal-100 text-teal-800',
      'protocole': 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[type] || colors['norme']}>{type.toUpperCase()}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button onClick={() => window.location.href = '/dashboard'} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold">Références Réglementaires</h1>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-blue-600">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Référence
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Normes, Lois et Protocoles Acoustiques</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && references.length === 0 ? (
              <div className="text-center py-8">Chargement...</div>
            ) : (
              <div className="space-y-3">
                {references.map((ref) => (
                  <div key={ref.reference_id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {getTypeBadge(ref.reference_type)}
                          {getStatusBadge(ref.status)}
                          <span className="font-mono text-sm font-semibold text-blue-600">{ref.code}</span>
                        </div>
                        <h3 className="font-semibold text-lg">{ref.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{ref.description}</p>
                        
                        {ref.explanation && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <strong>Explication:</strong> {ref.explanation}
                          </div>
                        )}
                        
                        {ref.tags && ref.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {ref.tags.map((tag, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-200 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(ref)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={(o) => { setShowModal(o); if (!o) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRef ? 'Modifier' : 'Nouvelle'} Référence</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type *</Label>
                  <Select value={form.reference_type} onValueChange={(v) => setForm({...form, reference_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="norme">Norme</SelectItem>
                      <SelectItem value="loi">Loi</SelectItem>
                      <SelectItem value="arrete">Arrêté</SelectItem>
                      <SelectItem value="protocole">Protocole</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Code/Référence *</Label>
                  <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Ex: NF S31-010" />
                </div>
              </div>

              <div>
                <Label>Titre *</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required rows={2} />
              </div>

              <div>
                <Label>Texte officiel</Label>
                <Textarea value={form.official_text} onChange={(e) => setForm({...form, official_text: e.target.value})} rows={2} />
              </div>

              <div>
                <Label>Explication (pour les clients)</Label>
                <Textarea value={form.explanation} onChange={(e) => setForm({...form, explanation: e.target.value})} rows={3} placeholder="Expliquer simplement ce que signifie cette référence..." />
              </div>

              <div>
                <Label>Protocole de mesure</Label>
                <Textarea value={form.measurement_protocol} onChange={(e) => setForm({...form, measurement_protocol: e.target.value})} rows={2} placeholder="Comment effectuer les mesures..." />
              </div>

              <div>
                <Label>Pourquoi c'est obligatoire</Label>
                <Textarea value={form.why_mandatory} onChange={(e) => setForm({...form, why_mandatory: e.target.value})} rows={2} placeholder="Raison de l'obligation..." />
              </div>

              <div>
                <Label>Tags (séparés par des virgules)</Label>
                <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="erp, musique, limite" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Annuler</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Sauvegarde...' : editingRef ? 'Modifier' : 'Créer'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RegulatoryReferencesManagement;
